import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { SunHorizon, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const HERO_IMG =
  "https://images.unsplash.com/photo-1668097613572-40b7c11c8727?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsJTIwaW5zdGFsbGF0aW9ufGVufDB8fHx8MTc3NjU4Nzc2NHww&ixlib=rb-4.1.0&q=85";

const QUICK_LOGINS = [
  { label: "Super Admin", email: "superadmin@solaroms.com", password: "SuperAdmin123!" },
  { label: "Sales", email: "sales1@solaroms.com", password: "Sales123!" },
  { label: "Admin", email: "admin1@solaroms.com", password: "Admin123!" },
  { label: "Finance", email: "finance1@solaroms.com", password: "Finance123!" },
  { label: "Warehouse", email: "warehouse1@solaroms.com", password: "Warehouse123!" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const fillQuick = (q) => {
    setEmail(q.email);
    setPassword(q.password);
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="login-page">
      {/* Left hero */}
      <div className="hidden lg:flex relative w-1/2 overflow-hidden border-r border-black">
        <img src={HERO_IMG} alt="Solar installation" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-3">
            <SunHorizon size={36} weight="duotone" color="#60A5FA" />
            <div className="font-display font-black text-2xl tracking-tight">SOLAR AFRICA</div>
          </div>
          <div>
            <div className="overline mb-3" style={{ color: "#60A5FA" }}>// Cworth Energy Nigeria</div>
            <h1 className="font-display font-black leading-[0.9] tracking-tighter text-5xl xl:text-6xl">
              Stop losing orders<br />
              in <span style={{ color: "#60A5FA" }}>WhatsApp threads.</span>
            </h1>
            <p className="mt-6 max-w-md text-white/80 text-base">
              One pipeline. Sales → Admin → Finance → Warehouse. Every order tracked, every handoff logged.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-px bg-white/20 max-w-md">
              {[
                { k: "100+", v: "Orders / day" },
                { k: "5", v: "Roles" },
                { k: "0", v: "Lost messages" },
              ].map((s) => (
                <div key={s.v} className="bg-black/40 p-4">
                  <div className="font-display font-black text-3xl text-white">{s.k}</div>
                  <div className="overline mt-1 text-white/70">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="overline text-white/60">Solar systems · Panels · Inverters · Batteries</div>
        </div>
      </div>

      {/* Right form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <div className="overline mb-2">// Sign in</div>
            <h2 className="font-display font-black text-4xl tracking-tighter">Order Control Room.</h2>
            <p className="text-sm text-[var(--muted)] mt-2">
              Use your work email and password. Quick-fill below for demo accounts.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="label-tech">Email</label>
              <input
                type="email"
                className="input-tech"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@solaroms.com"
                required
                data-testid="login-email"
              />
            </div>
            <div>
              <label className="label-tech">Password</label>
              <input
                type="password"
                className="input-tech"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="login-password"
              />
            </div>
            <button type="submit" disabled={loading} className="btn-solar w-full flex items-center justify-center gap-2" data-testid="login-submit">
              {loading ? "Signing in…" : "Enter Control Room"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </form>

          <div className="mt-10 border-t border-[var(--line)] pt-6">
            <div className="overline mb-3">Demo accounts</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {QUICK_LOGINS.map((q) => (
                <button
                  key={q.email}
                  type="button"
                  onClick={() => fillQuick(q)}
                  className="text-left border border-[var(--line)] hover:border-[var(--ink)] px-3 py-2 transition-colors"
                  data-testid={`quick-${q.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="text-[0.7rem] font-bold uppercase tracking-widest text-[var(--solar)]">{q.label}</div>
                  <div className="text-[0.7rem] text-[var(--muted)] truncate">{q.email}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
